import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import newBookingIllustration from '../../../../assets/images/new_booking_illustration.png';
import { useDictionary } from '../../../../utilities/hooks/UseDictionary';
import { UseCurrencyDecorator } from '../../../../utilities/hooks/UseCurrencyDecorator';
import { useProviderCurrency } from '../../../../utilities/hooks/useProviderCurrency';
import { AmountFormatHelper } from '../../../../utilities/helpers/AmountFormatHelper';
import ConfirmationModal from '../../common/ConfirmationModal';
import FormLoader from '../../common/FormLoader';
import CustomToast from '../../common/CustomToast';
import LinkButton from '../../common/LinkButton';
import CustomerSearchInput from '../../common/CustomerSearchInput';
import ProfileImage from '../../common/ProfileImage';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/ic_search.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/ic_plus_dark.svg';
import pencilIcon from '../../../../assets/icons/ic_pencil.svg';
import closeIcon from '../../../../assets/icons/ic_close.svg';
import checkWhiteIcon from '../../../../assets/icons/ic_check_white.svg';
import { DatePickerField } from '../custom/DatePickerField';
import { TimePickerField } from '../custom/TimePickerField';
import ExperienceSelect from './schedule_slot_dialog/experience_select/ExperienceSelect';
import DurationFields from './DurationFields';
import ParticipantStepper from './ParticipantStepper';
import { createOfflineBooking, updateOfflineBooking } from './networking';
import {
	getAvailableExperiencesList,
	getExperienceById
} from '../provider_experiences/networking';
import ConverterHelper from '../../../../utilities/helpers/Converters';
import moment from 'moment';

const isTrue = (value) =>
	value === true || value === 1 || value === '1';

const normalizeTimeValue = (time) => {
	if (!time) {
		return '';
	}

	return time.split(':').slice(0, 2).join(':');
};

// Compares two { groupId: quantity } maps regardless of key order / missing keys.
const areQuantitiesEqual = (a, b) => {
	const keys = new Set([
		...Object.keys(a || {}),
		...Object.keys(b || {})
	]);

	for (const key of keys) {
		if ((Number(a?.[key]) || 0) !== (Number(b?.[key]) || 0)) {
			return false;
		}
	}

	return true;
};

const NewBookingDialog = ({
	setShowDialog,
	onBookingCreated,
	mode,
	booking,
	slot
}) => {
	const isEditMode = mode === 'edit' && Boolean(booking);
	// Slot mode books into an existing calendar slot: the experience and the
	// slot's date/time/duration are fixed and shown read-only.
	const isSlotMode = !isEditMode && Boolean(slot);
	const dictionary = useDictionary();
	const currencyDecorator = UseCurrencyDecorator();
	const providerCurrency = useProviderCurrency();
	const [showDialogAnimation, setShowDialogAnimation] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [showDismissDialog, setDismissDialogVisibility] = useState(false);
	const [experiences, setExperiences] = useState([]);
	const [selectedExperienceOption, setSelectedExperienceOption] = useState(null);
	const [selectedExperience, setSelectedExperience] = useState(null);
	// Prices shown in the dialog are in the booked experience's currency, which
	// for the provider's own experiences equals their account currency.
	const currency =
		selectedExperience?.currency ?? booking?.currency ?? providerCurrency;
	const [date, setDate] = useState('');
	const [time, setTime] = useState('');
	const [durationDays, setDurationDays] = useState(0);
	const [durationHours, setDurationHours] = useState(0);
	const [durationMinutes, setDurationMinutes] = useState(0);
	const [customerNameValue, setCustomerNameValue] = useState('');
	const [customerEmailValue, setCustomerEmailValue] = useState('');
	const [customerPhoneValue, setCustomerPhoneValue] = useState('');
	const [customerNotesValue, setCustomerNotesValue] = useState('');
	// 'search' = pick an existing customer; 'new' = type a new one.
	const [customerMode, setCustomerMode] = useState('search');
	const [selectedCustomerId, setSelectedCustomerId] = useState(null);
	const [participantQuantities, setParticipantQuantities] = useState({});
	const [formErrors, setFormErrors] = useState({});
	const [manualAmount, setManualAmount] = useState(null);
	const [isEditingAmount, setIsEditingAmount] = useState(false);
	const [amountDraft, setAmountDraft] = useState('');
	const [amountJustChanged, setAmountJustChanged] = useState(false);
	const amountInputRef = useRef(null);
	// Snapshot of the booking's values at edit-mode load — used to send only the
	// fields the provider actually changed on update.
	const initialEditValues = useRef(null);

	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	const participantGroups = selectedExperience?.participant_groups || [];
	const totalParticipants = useMemo(
		() =>
			Object.values(participantQuantities).reduce(
				(total, quantity) => total + (Number(quantity) || 0),
				0
			),
		[participantQuantities]
	);
	const isOfflineEnabled = isTrue(selectedExperience?.allow_offline);
	const defaultDuration = Number(selectedExperience?.duration) || 0;
	const currentDuration =
		Number(durationDays || 0) * 24 * 60 * 60 +
		Number(durationHours || 0) * 60 * 60 +
		Number(durationMinutes || 0) * 60;
	const hasChanges =
		date !== '' ||
		time !== '' ||
		customerNameValue.trim() !== '' ||
		customerEmailValue.trim() !== '' ||
		customerPhoneValue.trim() !== '' ||
		customerNotesValue.trim() !== '' ||
		totalParticipants > 0 ||
		(selectedExperience ? currentDuration !== defaultDuration : false);
	const hasCustomerContact =
		customerEmailValue.trim() !== '' || customerPhoneValue.trim() !== '';
	const hasValidEmail =
		customerEmailValue.trim() === '' ||
		emailRegex.test(customerEmailValue.trim());
	const hasValidPhone =
		customerPhoneValue.trim() === '' ||
		(!isNaN(customerPhoneValue.trim()) &&
			customerPhoneValue.trim().length <= 20);
	const canSave = Boolean(
		!showLoader &&
			!isEditingAmount &&
			experiences.length > 0 &&
			selectedExperienceOption?.id &&
			selectedExperience &&
			date &&
			time &&
			customerNameValue.trim() !== '' &&
			hasCustomerContact &&
			hasValidEmail &&
			hasValidPhone &&
			currentDuration > 0 &&
			totalParticipants > 0
	);

	useEffect(() => {
		const source = axios.CancelToken.source();

		setTimeout(() => {
			setShowDialogAnimation(true);
		}, 50);

		if (isEditMode) {
			prefillFromBooking();
		} else if (isSlotMode) {
			prefillFromSlot();
		} else {
			getExperiencesAsync(source.token);
		}

		return () => {
			source.cancel('New booking dialog unmounted');
		};
	}, []);

	// Edit mode: hydrate every form field from the existing booking. The
	// experience itself is locked (the BE update endpoint cannot swap it), so
	// we skip the available-experiences list and load just this one.
	const prefillFromBooking = async () => {
		setShowLoader(true);

		try {
			const experience = booking.experience || {};
			const experienceOption = {
				id: experience.id,
				title: experience.title,
				color: experience.color,
				thumbnail_url: experience.assets?.[0]
			};
			setExperiences([experienceOption]);
			setSelectedExperienceOption(experienceOption);

			const { data } = await getExperienceById(experience.id);
			setSelectedExperience(data);

			// Duration comes from the booked slot, not the experience default —
			// the booking may have been created with a custom duration.
			const slotStart = Number(booking.slot?.start_time) || 0;
			const slotEnd = Number(booking.slot?.end_time) || 0;
			const slotDuration = slotEnd - slotStart;
			const duration =
				slotDuration > 0 ? slotDuration : Number(data?.duration) || 0;
			setDurationDays(Math.floor(duration / 86400));
			setDurationHours(Math.floor((duration % 86400) / 3600));
			setDurationMinutes(Math.floor((duration % 3600) / 60));

			let dateValue = '';
			let timeValue = '';
			if (slotStart) {
				const start = moment.unix(slotStart);
				dateValue = start.format('YYYY-MM-DD');
				timeValue = start.format('HH:mm');
				setDate(dateValue);
				setTime(timeValue);
			}

			// Participant quantities keyed by group id — seed every group from
			// the experience at 0, then overlay the booking's booked counts.
			const quantities = {};
			(data?.participant_groups || []).forEach((group) => {
				quantities[group.id] = 0;
			});
			(booking.participant_groups || []).forEach((group) => {
				quantities[group.id] = Number(group.quantity) || 0;
			});
			setParticipantQuantities(quantities);

			// Customer is locked in edit mode and rendered as a read-only
			// card from `booking.customer`. These values still seed the
			// contact state so validateForm()/canSave keep passing.
			const nameValue = booking.customer?.name || '';
			const emailValue = booking.customer?.email || '';
			const phoneValue = booking.customer?.phone
				? String(booking.customer.phone).replace(/^\+/, '')
				: '';
			setCustomerNameValue(nameValue);
			setCustomerEmailValue(emailValue);
			setCustomerPhoneValue(phoneValue);
			setCustomerMode('new');

			const note = booking.notes?.internal;
			const noteValue =
				typeof note === 'string'
					? note
					: note
					? Object.values(note).filter(Boolean).join('\n')
					: '';
			setCustomerNotesValue(noteValue);

			// Treat the stored amount as a manual override only when it differs
			// from the freshly-computed estimate (manual price or a discount).
			const estimate = (data?.participant_groups || []).reduce(
				(total, group) =>
					total +
					(Number(group.rate) || 0) * (Number(quantities[group.id]) || 0),
				0
			);
			const bookingAmount = Number(booking.amount) || 0;
			if (Math.round(estimate) !== Math.round(bookingAmount)) {
				setManualAmount(AmountFormatHelper.formatAmount(bookingAmount));
			}

			// Record the loaded values so onFinishClicked can send only what the
			// provider actually changes.
			initialEditValues.current = {
				date: dateValue,
				time: timeValue,
				// Minute-aligned to match the reconstructed `currentDuration`
				// (the day/hour/minute fields cannot represent leftover seconds).
				duration: Math.floor(duration / 60) * 60,
				quantities,
				notes: noteValue,
				amount: bookingAmount
			};
		} catch (error) {
			if (error?.response?.data?.message) {
				toast.error(<CustomToast message={error.response.data.message} />, {
					position: 'bottom-left',
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: false,
					progress: undefined,
					closeButton: false,
					icon: false,
					className: 'toast-white-error'
				});
			}
		} finally {
			setShowLoader(false);
		}
	};

	// Slot mode: the booking attaches to an existing slot. The experience and
	// the slot's date/time/duration are fixed (rendered read-only) — only the
	// customer, participants, notes and amount stay editable. The experience
	// list is skipped; just this one experience is loaded for its groups/rates.
	const prefillFromSlot = async () => {
		setShowLoader(true);

		try {
			const experience = slot.experience || {};
			const experienceOption = {
				id: experience.id,
				title: experience.title,
				color: experience.color,
				thumbnail_url: experience.assets?.[0]
			};
			setExperiences([experienceOption]);
			setSelectedExperienceOption(experienceOption);

			const { data } = await getExperienceById(experience.id);
			setSelectedExperience(data);

			// Duration / date / time come straight from the slot — these are
			// display-only and never part of the payload (slot_id is sent).
			const slotStart = Number(slot.start_time) || 0;
			const slotEnd = Number(slot.end_time) || 0;
			const duration =
				Number(slot.duration) ||
				(slotEnd > slotStart ? slotEnd - slotStart : 0) ||
				Number(data?.duration) ||
				0;
			setDurationDays(Math.floor(duration / 86400));
			setDurationHours(Math.floor((duration % 86400) / 3600));
			setDurationMinutes(Math.floor((duration % 3600) / 60));

			if (slotStart) {
				const start = moment.unix(slotStart);
				setDate(start.format('YYYY-MM-DD'));
				setTime(start.format('HH:mm'));
			}

			// Seed every participant group from the experience at 0.
			const quantities = {};
			(data?.participant_groups || []).forEach((group) => {
				quantities[group.id] = 0;
			});
			setParticipantQuantities(quantities);
		} catch (error) {
			showErrorToast(error);
		} finally {
			setShowLoader(false);
		}
	};

	const getExperiencesAsync = async (cancelToken) => {
		setShowLoader(true);

		try {
			const { data } = await getAvailableExperiencesList(cancelToken);
			const items = data?.data || [];
			setExperiences(items);

			if (items.length > 0) {
				await onExperienceSelected(items[0]);
			}
		} catch (error) {
			if (!axios.isCancel(error) && error?.response?.data?.message) {
				toast.error(<CustomToast message={error.response.data.message} />, {
					position: 'bottom-left',
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: false,
					progress: undefined,
					closeButton: false,
					icon: false,
					className: 'toast-white-error'
				});
			}
		} finally {
			setShowLoader(false);
		}
	};

	const onExperienceSelected = async (experienceOption) => {
		setSelectedExperienceOption(experienceOption);
		setSelectedExperience(null);
		setParticipantQuantities({});
		setDurationDays(0);
		setDurationHours(0);
		setDurationMinutes(0);
		setManualAmount(null);
		setIsEditingAmount(false);
		setAmountDraft('');
		setAmountJustChanged(false);
		setFormErrors((prevErrors) => ({
			...prevErrors,
			experience: '',
			participants: '',
			offline: '',
			duration: ''
		}));

		setShowLoader(true);

		try {
			const { data } = await getExperienceById(experienceOption.id);
			setSelectedExperience(data);
			const duration = Number(data?.duration) || 0;
			setDurationDays(Math.floor(duration / 86400));
			setDurationHours(Math.floor((duration % 86400) / 3600));
			setDurationMinutes(Math.floor((duration % 3600) / 60));

			const nextQuantities = {};
			(data?.participant_groups || []).forEach((group) => {
				nextQuantities[group.id] = 0;
			});
			setParticipantQuantities(nextQuantities);
		} catch (error) {
			if (error?.response?.data?.message) {
				toast.error(<CustomToast message={error.response.data.message} />, {
					position: 'bottom-left',
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: false,
					progress: undefined,
					closeButton: false,
					icon: false,
					className: 'toast-white-error'
				});
			}
		} finally {
			setShowLoader(false);
		}
	};

	const clearCustomerFields = () => {
		setCustomerNameValue('');
		setCustomerEmailValue('');
		setCustomerPhoneValue('');
		setSelectedCustomerId(null);
		setFormErrors((prevErrors) => ({
			...prevErrors,
			customerName: '',
			customerContact: '',
			customerEmail: '',
			customerPhone: ''
		}));
	};

	// Toggling between search / new entry starts the customer from scratch.
	const switchToNewCustomer = () => {
		clearCustomerFields();
		setCustomerMode('new');
	};

	const switchToSearchCustomer = () => {
		clearCustomerFields();
		setCustomerMode('search');
	};

	// A picked customer feeds the same contact fields a typed customer would;
	// `null` is sent on every keystroke/clear so a stale pick can't linger.
	const onCustomerSelected = (customer) => {
		if (!customer) {
			clearCustomerFields();
			return;
		}

		setCustomerNameValue(customer.name || '');
		setCustomerEmailValue(customer.email || '');
		setCustomerPhoneValue(
			customer.phone ? String(customer.phone).replace(/^\+/, '') : ''
		);
		setSelectedCustomerId(customer.id || null);
		setFormErrors((prevErrors) => ({
			...prevErrors,
			customerName: '',
			customerContact: '',
			customerEmail: '',
			customerPhone: ''
		}));
	};

	const onCancelClicked = () => {
		if (hasChanges) {
			setDismissDialogVisibility(true);
			return;
		}

		dismissDialogBox();
	};

	const dismissDialogBox = () => {
		setShowDialogAnimation(false);
		setTimeout(() => {
			setShowDialog(false);
		}, 600);
	};

	const onDateChanged = (value) => {
		setDate(moment(value).format('YYYY-MM-DD'));
		setFormErrors((prevErrors) => ({
			...prevErrors,
			date: ''
		}));
	};

	const onTimeChanged = (value) => {
		setTime(value);
		setFormErrors((prevErrors) => ({
			...prevErrors,
			time: ''
		}));
	};

	const onParticipantQuantityChanged = (groupId, value) => {
		const numericValue = value === '' ? '' : Math.max(0, Number(value));

		setParticipantQuantities((prevState) => ({
			...prevState,
			[groupId]: numericValue
		}));
		setFormErrors((prevErrors) => ({
			...prevErrors,
			participants: ''
		}));
	};

	const validateForm = () => {
		const nextErrors = {};

		if (!selectedExperienceOption?.id) {
			nextErrors.experience = dictionary.requiredTemplate(dictionary.experience);
		}

		if (!date) {
			nextErrors.date = dictionary.requiredTemplate(dictionary.date);
		}

		if (!time) {
			nextErrors.time = dictionary.requiredTemplate(dictionary.time);
		}

		if (currentDuration <= 0) {
			nextErrors.duration = dictionary.requiredTemplate(dictionary.duration);
		}

		if (customerNameValue.trim() === '') {
			nextErrors.customerName = dictionary.requiredTemplate(
				dictionary.providerName
			);
		}

		if (!customerEmailValue.trim() && !customerPhoneValue.trim()) {
			nextErrors.customerContact = dictionary.requiredTemplate(
				`${dictionary.providerEmail} / ${dictionary.providerPhoneNumber}`
			);
		}

		if (customerEmailValue.trim() && !emailRegex.test(customerEmailValue.trim())) {
			nextErrors.customerEmail = dictionary.invalid_email;
		}

		if (
			customerPhoneValue.trim() &&
			(isNaN(customerPhoneValue.trim()) ||
				customerPhoneValue.trim().length > 20)
		) {
			nextErrors.customerPhone = dictionary.phone_length_error;
		}

		if (totalParticipants <= 0) {
			nextErrors.participants = dictionary.create_booking_participants_required;
		}

		setFormErrors(nextErrors);

		return Object.keys(nextErrors).length === 0;
	};

	const showErrorToast = (error) => {
		if (error?.response?.data?.message) {
			toast.error(<CustomToast message={error.response.data.message} />, {
				position: 'bottom-left',
				autoClose: 5000,
				hideProgressBar: true,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: false,
				progress: undefined,
				closeButton: false,
				icon: false,
				className: 'toast-white-error'
			});
		}
	};

	const onFinishClicked = async () => {
		if (!validateForm()) {
			return;
		}

		const priceItems = participantGroups
			.map((group) => ({
				id: group.id,
				quantity: Number(participantQuantities[group.id] || 0)
			}))
			.filter((group) => group.quantity > 0);

		const amount =
			manualAmount !== null
				? AmountFormatHelper.deFormatAmount(Number(manualAmount))
				: undefined;

		// Raw contact fields the provider typed (used when no existing customer
		// was picked from search).
		const rawContact = {
			contact_name: customerNameValue.trim(),
			contact_email: customerEmailValue.trim() || undefined,
			contact_phone: customerPhoneValue.trim()
				? `+${customerPhoneValue.trim()}`
				: undefined
		};

		// Edit mode: send only the fields the provider actually changed. The BE
		// update is partial and every field carries a side effect (re-creating
		// the slot, recreating participants, re-syncing the contact), so an
		// unchanged value must not be resent.
		if (isEditMode) {
			const initial = initialEditValues.current || {};
			const payload = {};

			const slotChanged =
				date !== initial.date ||
				normalizeTimeValue(time) !==
					normalizeTimeValue(initial.time || '') ||
				currentDuration !== initial.duration;
			if (slotChanged) {
				// The BE requires date/time/duration together or not at all.
				payload.slot_date = date;
				payload.slot_time = normalizeTimeValue(time);
				payload.duration = currentDuration;
			}

			const priceItemsChanged = !areQuantitiesEqual(
				participantQuantities,
				initial.quantities
			);
			if (priceItemsChanged) {
				payload.price_items = priceItems;
			}

			// Contact is locked in edit mode (displayed read-only) — it is
			// never part of the update payload. Changing or replacing a
			// contact is done from the contact page instead.

			if (customerNotesValue.trim() !== (initial.notes || '').trim()) {
				payload.notes = customerNotesValue.trim();
			}

			// Resend the manual amount when it was edited, or when the price
			// items changed (otherwise the BE recomputes over the override).
			if (manualAmount !== null) {
				const amountChanged =
					Math.round(amount) !== Math.round(initial.amount || 0);
				if (amountChanged || priceItemsChanged) {
					payload.amount = amount;
				}
			}

			if (Object.keys(payload).length === 0) {
				// Nothing changed — close without an update call.
				setShowDialog(false);
				return;
			}

			setShowLoader(true);
			try {
				const { data } = await updateOfflineBooking(booking.id, payload);
				setShowDialog(false);
				onBookingCreated(data);
			} catch (error) {
				showErrorToast(error);
			} finally {
				setShowLoader(false);
			}
			return;
		}

		// Create mode.
		const contactFields = selectedCustomerId
			? { provider_contact_id: selectedCustomerId }
			: rawContact;

		// Slot mode books into the existing slot — send slot_id so the BE keeps
		// the slot's date/time/duration. Otherwise a new slot is created from
		// the date/time/duration the provider entered.
		const slotFields = isSlotMode
			? { slot_id: slot.id }
			: {
					slot_date: date,
					slot_time: normalizeTimeValue(time),
					duration: currentDuration
			  };

		setShowLoader(true);
		try {
			const { data } = await createOfflineBooking({
				experience_id: selectedExperienceOption.id,
				...slotFields,
				price_items: priceItems,
				...contactFields,
				notes: customerNotesValue.trim() || undefined,
				auto_enable_offline: !isOfflineEnabled ? true : undefined,
				amount
			});
			setShowDialog(false);
			onBookingCreated(data);
		} catch (error) {
			showErrorToast(error);
		} finally {
			setShowLoader(false);
		}
	};

	const estimatedAmount = participantGroups.reduce(
		(total, group) =>
			total +
			(Number(group.rate) || 0) *
				(Number(participantQuantities[group.id]) || 0),
		0
	);

	const displayedAmount =
		manualAmount !== null
			? AmountFormatHelper.deFormatAmount(Number(manualAmount))
			: estimatedAmount;

	const startEditingAmount = () => {
		const seed =
			manualAmount !== null
				? manualAmount
				: AmountFormatHelper.formatAmount(estimatedAmount);
		setAmountDraft(String(seed));
		setIsEditingAmount(true);
	};

	const commitAmount = () => {
		const n = Number(amountDraft);
		if (amountDraft !== '' && !isNaN(n) && n >= 0) {
			setManualAmount(n);
			setAmountJustChanged(true);
			setTimeout(() => setAmountJustChanged(false), 700);
		}
		setIsEditingAmount(false);
	};

	const cancelAmountEdit = () => {
		setIsEditingAmount(false);
		setAmountDraft('');
	};

	const resetAmount = () => {
		setManualAmount(null);
		setAmountDraft('');
	};

	useEffect(() => {
		if (isEditingAmount && amountInputRef.current) {
			amountInputRef.current.focus();
			amountInputRef.current.select();
		}
	}, [isEditingAmount]);

	return (
		<div
			className={`Customer-wrapper group-category-tags-wrapper${
				showDialogAnimation ? ' group-category-tags-wrapper-visible' : ''
			}`}
			style={{
				position: 'fixed',
				top: 0,
				zIndex: 11
			}}
		>
			<div className="group-dialog-content-wrapper thin-scrollbar">
				<div
					className="dialog-close-button"
					style={{ right: '24px', top: '24px' }}
					onClick={onCancelClicked}
				/>
				<img src={newBookingIllustration} alt="new booking" />
				<div className="group-dialog-content-container group-dialog-content-form-container">
					<h2 className="new-booking-dialog-title">
						{isEditMode
							? dictionary.edit_booking
							: dictionary.create_new_booking}
					</h2>

					{!isEditMode && selectedExperience && !isOfflineEnabled ? (
						<div className="warning-container" style={{ marginBottom: 16 }}>
							{dictionary.create_booking_offline_channel_required}
						</div>
					) : null}

					{experiences.length > 0 ? (
						<div style={{ marginBottom: 16 }}>
							<ExperienceSelect
								value={selectedExperienceOption}
								setValue={onExperienceSelected}
								items={experiences}
								disabled={isEditMode || isSlotMode}
							/>
							{formErrors.experience ? (
								<small className="input-error-message">
									{formErrors.experience}
								</small>
							) : null}
						</div>
					) : (
						<div className="warning-container" style={{ marginBottom: 16 }}>
							{dictionary.create_booking_no_available_experiences}
						</div>
					)}

					{selectedExperience ? (
						<>
							{isSlotMode ? (
								<div className="new-booking-slot-fields">
									<div className="new-booking-contact-row">
										<span className="new-booking-contact-row-label">
											{dictionary.date}
										</span>
										<span className="new-booking-contact-row-name">
											{date
												? moment(date).format('ddd, MMM D, YYYY')
												: '-'}
										</span>
									</div>
									<div className="new-booking-contact-row">
										<span className="new-booking-contact-row-label">
											{dictionary.time}
										</span>
										<span className="new-booking-contact-row-name">
											{time || '-'}
										</span>
									</div>
									<div className="new-booking-contact-row">
										<span className="new-booking-contact-row-label">
											{dictionary.duration}
										</span>
										<span className="new-booking-contact-row-name">
											{ConverterHelper.secondsToDayHourMinute(
												currentDuration,
												dictionary
											)}
										</span>
									</div>
								</div>
							) : (
								<>
									<div
										className="group-inputs"
										style={{ marginTop: 0, marginBottom: 16 }}
									>
										<label style={{ marginBottom: 8 }}>
											{dictionary.date}
										</label>
										<DatePickerField
											placeholderText={dictionary.placeholder_select_date}
											value={date}
											dateFormat="EEE, MMM d, yyyy"
											onChange={onDateChanged}
										/>
										{formErrors.date ? (
											<small className="input-error-message">
												{formErrors.date}
											</small>
										) : null}
									</div>

									<div
										className="group-inputs"
										style={{ marginTop: 0, marginBottom: 16 }}
									>
										<label style={{ marginBottom: 8 }}>
											{dictionary.time}
										</label>
										<TimePickerField value={time} onChange={onTimeChanged} />
										{formErrors.time ? (
											<small className="input-error-message">
												{formErrors.time}
											</small>
										) : null}
									</div>

									<label style={{ marginBottom: 8 }}>
										{dictionary.duration}
									</label>
									<DurationFields
										days={durationDays}
										hours={durationHours}
										minutes={durationMinutes}
										setDays={(value) => {
											setDurationDays(value);
											setFormErrors((prevErrors) => ({
												...prevErrors,
												duration: ''
											}));
										}}
										setHours={(value) => {
											setDurationHours(value);
											setFormErrors((prevErrors) => ({
												...prevErrors,
												duration: ''
											}));
										}}
										setMinutes={(value) => {
											setDurationMinutes(value);
											setFormErrors((prevErrors) => ({
												...prevErrors,
												duration: ''
											}));
										}}
										inputTestIdPrefix="new-booking"
									/>
									{formErrors.duration ? (
										<small
											className="input-error-message"
											style={{ marginTop: -8, marginBottom: 16 }}
										>
											{formErrors.duration}
										</small>
									) : null}
								</>
							)}

							{/*
								Edit mode locks the contact: it is displayed
								read-only as a single Customer row (avatar +
								name), matching the booking detail page. Editing
								or replacing a contact belongs on the contact
								page, not here.
							*/}
							{isEditMode ? (
								<>
									<h3 className="new-booking-dialog-section-title">
										{dictionary.customer}
									</h3>
									<div className="new-booking-contact-row">
										<span className="new-booking-contact-row-label">
											{dictionary.full_name}
										</span>
										<div className="new-booking-contact-row-value">
											<ProfileImage
												name={booking.customer?.name}
												photo={booking.customer?.avatar_url}
												size={{
													width: 24,
													height: 24,
													fontSize: 11,
													lineHeight: 14
												}}
											/>
											<span
												className="new-booking-contact-row-name"
												title={booking.customer?.name}
											>
												{booking.customer?.name}
											</span>
										</div>
									</div>
								</>
							) : (
								<div className="new-booking-section-header">
									<h3 className="new-booking-dialog-section-title">
										{dictionary.customer}
									</h3>
									{customerMode === 'search' ? (
										<LinkButton
											icon={PlusIcon}
											label={dictionary.new_customer}
											onClick={switchToNewCustomer}
											testId="new-customer-link"
										/>
									) : (
										<LinkButton
											icon={SearchIcon}
											label={dictionary.search_customer}
											onClick={switchToSearchCustomer}
											testId="search-customer-link"
										/>
									)}
								</div>
							)}

							{isEditMode ? null : customerMode === 'search' ? (
								<div
									className="customer-search-group"
									style={{ marginBottom: 16 }}
								>
									<label style={{ marginBottom: 8 }}>
										{dictionary.full_name}
									</label>
									<CustomerSearchInput
										placeholder={
											dictionary.search_by_provider_customers_name
										}
										onSelect={onCustomerSelected}
										testId="customer-search-input"
									/>
									{formErrors.customerName ? (
										<small className="input-error-message">
											{formErrors.customerName}
										</small>
									) : null}
									{formErrors.customerContact ? (
										<small className="input-error-message">
											{formErrors.customerContact}
										</small>
									) : null}
								</div>
							) : (
								<>
									<div
										className="group-inputs text-name-wrapper"
										style={{ marginTop: 0, marginBottom: 16 }}
									>
										<label style={{ marginBottom: 8 }}>
											{dictionary.providerName}
										</label>
										<input
											type="text"
											maxLength="40"
											placeholder={dictionary.placeholder_customer_full_name}
											value={customerNameValue}
											onChange={(event) => {
												setCustomerNameValue(event.target.value);
												setFormErrors((prevErrors) => ({
													...prevErrors,
													customerName: ''
												}));
											}}
										/>
										{formErrors.customerName ? (
											<small className="input-error-message">
												{formErrors.customerName}
											</small>
										) : null}
									</div>

									<div
										className="group-inputs text-name-wrapper"
										style={{ marginTop: 0, marginBottom: 16 }}
									>
										<label style={{ marginBottom: 8 }}>
											{dictionary.providerEmail}
										</label>
										<input
											type="text"
											maxLength="50"
											placeholder={dictionary.placeholder_enter_email}
											value={customerEmailValue}
											onChange={(event) => {
												setCustomerEmailValue(event.target.value);
												setFormErrors((prevErrors) => ({
													...prevErrors,
													customerContact: '',
													customerEmail: ''
												}));
											}}
										/>
										{formErrors.customerEmail ? (
											<small className="input-error-message">
												{formErrors.customerEmail}
											</small>
										) : null}
									</div>

									<div
										className="group-inputs text-name-wrapper provider-customers-number-input"
										style={{ marginTop: 0, marginBottom: 16 }}
									>
										<label style={{ marginBottom: 8 }}>
											{dictionary.providerPhoneNumber}
										</label>
										<PhoneInput
											country={'gr'}
											placeholder={dictionary.placeholder_phone_format}
											value={customerPhoneValue}
											onChange={(value) => {
												setCustomerPhoneValue(value);
												setFormErrors((prevErrors) => ({
													...prevErrors,
													customerContact: '',
													customerPhone: ''
												}));
											}}
										/>
										{formErrors.customerPhone ? (
											<small className="input-error-message">
												{formErrors.customerPhone}
											</small>
										) : null}
										{formErrors.customerContact ? (
											<small className="input-error-message">
												{formErrors.customerContact}
											</small>
										) : null}
									</div>
								</>
							)}

							<h3 className="new-booking-dialog-section-title">
								{dictionary.participants}
							</h3>

							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '12px',
									marginBottom: '12px'
								}}
							>
								{participantGroups.map((group) => {
									const minAge = Number(group.min_age) || 0;
									const maxAge = Number(group.max_age) || 0;
									const subtitle =
										maxAge > 0 && maxAge < 99
											? dictionary.agesRangeTemplate(minAge, maxAge)
											: dictionary.agesAndOlderTemplate(minAge);

									return (
										<div key={group.id} className="participant-row">
											<div className="participant-row-info">
												<div className="participant-row-name">
													{group.name}
												</div>
												<div className="participant-row-subtitle">
													{subtitle}
												</div>
											</div>
											<div className="participant-row-price">
												{currencyDecorator(
													AmountFormatHelper.formatAmount(group.rate),
													currency
												)}
												/{dictionary.seat}
											</div>
											<ParticipantStepper
												value={participantQuantities[group.id] ?? 0}
												onChange={(nextValue) =>
													onParticipantQuantityChanged(group.id, nextValue)
												}
											/>
										</div>
									);
								})}
							</div>

							<div className="text" style={{ marginBottom: 16, opacity: 0.7 }}>
								{totalParticipants} /{' '}
								{selectedExperience.maximum_group_size || 0}{' '}
								{dictionary.maximum_group_size.toLowerCase()}
							</div>

							{formErrors.participants ? (
								<small
									className="input-error-message"
									style={{ marginTop: -8, marginBottom: 16 }}
								>
									{formErrors.participants}
								</small>
							) : null}

							<h3 className="new-booking-dialog-section-title">
								{dictionary.notes}
							</h3>

							<div
								className="group-inputs text-name-wrapper"
								style={{ marginTop: 0, marginBottom: 16 }}
							>
								<label style={{ marginBottom: 8 }}>{dictionary.notes}</label>
								<textarea
									rows="4"
									cols="50"
									maxLength="255"
									onChange={(event) => setCustomerNotesValue(event.target.value)}
									value={customerNotesValue}
									style={{ marginTop: 0 }}
								/>
							</div>
						</>
					) : null}
				</div>
			</div>

			<FormLoader
				onDismissClicked={onCancelClicked}
				showMessage={false}
				message={null}
				rounded={true}
				showLoader={showLoader}
				showEnter={false}
			/>

			<div
				className="group-dialog-button-container"
				style={{
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '12px 24px'
				}}
			>
				{isEditingAmount ? (
					<div className="new-booking-amount-edit">
						<span className="new-booking-amount-label">
							{dictionary.Total}:
						</span>
						<input
							ref={amountInputRef}
							className="new-booking-amount-input"
							type="number"
							min="0"
							value={amountDraft}
							onChange={(e) => setAmountDraft(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault();
									commitAmount();
								}
								if (e.key === 'Escape') {
									e.preventDefault();
									cancelAmountEdit();
								}
							}}
						/>
						<button
							type="button"
							className="new-booking-amount-iconbtn"
							onClick={cancelAmountEdit}
							aria-label={dictionary.cancel}
						>
							<img src={closeIcon} alt="" />
						</button>
						<button
							type="button"
							className="new-booking-amount-iconbtn new-booking-amount-iconbtn--ok"
							onClick={commitAmount}
							aria-label={dictionary.save}
						>
							<img src={checkWhiteIcon} alt="" />
						</button>
					</div>
				) : (
					<>
						<div className="new-booking-dialog-total new-booking-dialog-total--stack">
							<div className="new-booking-dialog-total-row">
								<span className="new-booking-amount-label">
									{dictionary.Total}:
								</span>
								<span
									className={
										'new-booking-dialog-total-value' +
										(amountJustChanged
											? ' new-booking-dialog-total-value--just-changed'
											: '')
									}
								>
									{currencyDecorator(
										AmountFormatHelper.formatAmount(displayedAmount),
										currency
									)}
								</span>
								{manualAmount === null ? (
									<button
										type="button"
										className="new-booking-amount-editbtn"
										onClick={startEditingAmount}
										aria-label={dictionary.edit_total}
									>
										<img src={pencilIcon} alt="" />
									</button>
								) : (
									<span className="new-booking-custom-chip">
										{dictionary.custom}
									</span>
								)}
							</div>
							{manualAmount !== null && (
								<div className="new-booking-amount-meta">
									<span className="new-booking-amount-auto">
										{dictionary.auto}&nbsp;
										{currencyDecorator(
											AmountFormatHelper.formatAmount(estimatedAmount),
											currency
										)}
									</span>
									<button
										type="button"
										className="new-booking-amount-reset"
										onClick={resetAmount}
									>
										{dictionary.reset}
									</button>
								</div>
							)}
						</div>
						<button
							disabled={!canSave}
							onClick={onFinishClicked}
							className={canSave ? 'button' : 'button button-disabled'}
						>
							{isEditMode ? dictionary.save : dictionary.create}
						</button>
					</>
				)}
			</div>

			{showDismissDialog ? (
				<ConfirmationModal
					setShowModal={setDismissDialogVisibility}
					asyncTask={dismissDialogBox}
					title={dictionary.dismiss_dialog}
					action={dictionary.dismiss}
				/>
			) : null}
		</div>
	);
};

NewBookingDialog.propTypes = {
	setShowDialog: PropTypes.func.isRequired,
	onBookingCreated: PropTypes.func.isRequired,
	mode: PropTypes.oneOf(['create', 'edit']),
	booking: PropTypes.object,
	slot: PropTypes.object
};

NewBookingDialog.defaultProps = {
	mode: 'create',
	booking: null,
	slot: null
};

export default NewBookingDialog;
